import { useLocation } from 'react-router-dom';
import React from 'react';

import type { Routes } from '../../../router/types';

import {
  carrySignInReturnTarget,
  signInReturnTargetFrom,
} from '../../../router/signInReturnTarget';
import { useScopedPendingQuestionnaires } from '../../../hooks/questionnaireSubmissions';
import { Navigate } from '../../../router/components';
import { usePathname } from '../../../router/hooks';
import { useAuthContext } from '../../../context';
import { Spinner } from '../../../components';

export interface AuthenticatedGuardProps {
  children: React.ReactNode;
}

const AuthenticatedGuard = ({ children }: AuthenticatedGuardProps) => {
  const pathname = usePathname();
  const location = useLocation();
  const {
    currentUser,
    hasEndedSession,
    isAuthenticated,
    isClientOnboardingRequired,
    isLoading,
    isPendingMemberInvite,
  } = useAuthContext();
  const { blockingAnywhere, blockingHere, isResolved } =
    useScopedPendingQuestionnaires();
  // Memoized because `Navigate` navigates again whenever its `state` changes identity.
  const hereAsReturnTarget = React.useMemo(
    () => signInReturnTargetFrom(location),
    [location],
  );
  const carriedReturnTarget = React.useMemo(
    () => carrySignInReturnTarget(location.state),
    [location.state],
  );

  if (isLoading) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <Spinner size="large" />
      </div>
    );
  }

  /**
   * We allow accept-invite to happen even when logged in.
   */
  if (pathname.startsWith('/auth/accept-invite')) {
    return children;
  }

  if (!isAuthenticated && !pathname.startsWith('/auth')) {
    // Only for somebody who ARRIVED signed out. A session that ended on this tab renders
    // here too, before `signOutAsync`'s own navigation lands, and the next person to sign
    // in must not be brought back to the page it left.
    return (
      <Navigate
        state={hasEndedSession ? undefined : hereAsReturnTarget}
        to="/auth/login"
        replace
      />
    );
  }

  if (!isAuthenticated && pathname.startsWith('/auth/onboarding')) {
    return <Navigate to="/auth/login" replace />;
  }

  /**
   * Same for the corp password screen, and for a sharper reason: it rewrites the
   * credential of a session it needs to already hold. Without one its submit returns
   * before it sends anything and its Retry resolves `undefined`, so a visitor who lands
   * here signed out — a sign-out in another tab, a bookmarked URL — sits in front of a
   * form that can never do anything.
   */
  if (!isAuthenticated && pathname.startsWith('/auth/set-corp-password')) {
    return <Navigate to="/auth/login" replace />;
  }

  /**
   * Corp users register with a one-time code and must set a new password
   * before accessing anything else in the app. This takes priority over every
   * other redirect below (onboarding, verification, etc.) — otherwise a corp
   * user who is ACTIVE but has not finished onboarding would bounce between
   * set-corp-password and onboarding in an infinite loop, rendering a blank page.
   */
  if (currentUser?.mustChangePassword) {
    if (!pathname.startsWith('/auth/set-corp-password')) {
      return <Navigate to="/auth/set-corp-password" replace />;
    }
    return children;
  }

  if (
    currentUser &&
    currentUser.status === 'ACTIVE' &&
    pathname.startsWith('/auth') &&
    !pathname.startsWith('/auth/onboarding')
    // `/auth/set-corp-password` is deliberately NOT excluded here. The forced flow is
    // the only flow: an account that still carries `mustChangePassword` is held on the
    // page by the rule above, and anybody else typing the URL belongs in the app. That
    // is also what removes the one way the screen could be reached from a tab holding a
    // socket, where the backend's revocation signs both tabs out (auth-2 hypothesis H);
    // `PATCH me/corp-password` refuses the same case.
  ) {
    // Through `/`, whatever the target: the rules below decide if it may be honoured.
    return <Navigate state={carriedReturnTarget} replace to="/" />;
  }

  if (
    currentUser &&
    currentUser.status === 'PENDING' &&
    !pathname.startsWith('/verification')
  ) {
    return <Navigate to="/verification" replace />;
  }

  if (
    isClientOnboardingRequired &&
    !pathname.startsWith('/auth/onboarding/client')
  ) {
    return <Navigate to="/auth/onboarding/client" replace />;
  }

  /**
   * A client whose fitness profile is already complete but who has picked up
   * a NEW pending questionnaire later (e.g. a second org invite, or a
   * scheduled questionnaire whose deadline has passed) doesn't hit
   * `isClientOnboardingRequired` above — route them back into the client
   * onboarding wizard, which jumps straight to the pending-questionnaires
   * step in this case (see `ClientOnboarding.tsx`'s `startIndex`).
   * `blockingHere` only counts direct/invite-assigned questionnaires or
   * overdue scheduled ones — see `isBlockingPendingQuestionnaire`. Non-overdue
   * scheduled questionnaires are surfaced via
   * `PendingScheduleQuestionnaireModal` instead and never reach this redirect.
   *
   * Scoped to the organization in context: a form assigned by organization B
   * is part of the B relationship and must not lock somebody out of
   * organization A, where they may well be the trainer. B's forms are offered
   * in the same dismissible modal instead.
   */
  if (
    currentUser &&
    currentUser.status === 'ACTIVE' &&
    blockingHere &&
    !pathname.startsWith('/auth/onboarding/client')
  ) {
    return <Navigate to="/auth/onboarding/client" replace />;
  }

  /**
   * Corp users are always end users (clients). They don't get to pick an
   * account type (organization / trainer / org member), so we send them
   * straight to client onboarding and keep them off the type-selection screen.
   */
  if (
    currentUser &&
    currentUser.corpOrganizationId &&
    currentUser.status === 'ACTIVE' &&
    !currentUser.hasFinishedOnboarding &&
    !pathname.startsWith('/auth/onboarding/client')
  ) {
    return <Navigate to="/auth/onboarding/client" replace />;
  }

  if (
    currentUser &&
    currentUser.status === 'ACTIVE' &&
    !currentUser.hasFinishedOnboarding &&
    // P1 (platform-user-class F2): the consumer onboarding wizard is not for
    // platform accounts — its organization/trainer branches would create an
    // org membership; the BE rejects them with 403 anyway.
    !currentUser.isPlatformUser &&
    !pathname.startsWith('/auth/onboarding')
  ) {
    return <Navigate to="/auth/onboarding" replace />;
  }
  // `blockingAnywhere`, not `blockingHere`: entering the wizard is scoped to
  // one organization, staying in it is not. A client who walked in — or was
  // sent here by accepting another organization's invite — clears everything
  // they owe in one pass instead of being bounced out halfway.
  //
  // `isResolved` matters on a full reload inside the wizard: the pending list
  // is not fetched yet, and treating "not known yet" as "nothing owed" would
  // eject the user to "/" before the data arrives. Falling through renders
  // the wizard, which shows its own loading state.
  if (
    currentUser &&
    currentUser.status === 'ACTIVE' &&
    pathname.startsWith('/auth/onboarding') &&
    !isClientOnboardingRequired &&
    isResolved &&
    !blockingAnywhere &&
    currentUser.hasFinishedOnboarding &&
    // A `member` finishing onboarding flips `hasFinishedOnboarding` here, on
    // `/auth/onboarding`, before its own `navigate('/pending-invitation')`
    // takes effect — without this exemption that render race sends them
    // home instead. See `isPendingMemberInvite`.
    !isPendingMemberInvite
  ) {
    return <Navigate replace to="/" />;
  }

  // The waiting page is for a `member` with no accepted invitation only. The
  // accept flow redirects some invitation types itself (CLIENT, TRAINER,
  // OWNER) but not all of them, and a reload re-renders whatever the URL
  // says — so this rule, not the accept flow, is what lets a member who
  // accepted leave, whichever invitation type it was.
  if (
    currentUser &&
    currentUser.status === 'ACTIVE' &&
    currentUser.hasFinishedOnboarding &&
    pathname === '/pending-invitation' &&
    !isPendingMemberInvite
  ) {
    return <Navigate replace to="/" />;
  }

  /**
   * The LAST rule, so every redirect above goes first: verification, the forced password
   * change, onboarding, a blocking questionnaire. Each of those drops the target on its way
   * out, and the person lands where that flow finishes.
   *
   * Only on `/`, because a deep page's own history state can never mean this, and the
   * redirect carries no state, so the target is spent here either way.
   *
   * A member still waiting for an invitation belongs nowhere else (`isPendingMemberInvite`),
   * so it stays home, and with the target gone: accepting the invitation later must not
   * send it to a link from before. A platform account has the same shape, no membership and
   * no fitness profile, and is not one: it has rights to whatever page it opened (P1, P5).
   */
  const returnTarget = carriedReturnTarget?.signInReturnTo;
  if (returnTarget && pathname === '/' && currentUser?.status === 'ACTIVE') {
    const isHonoured = currentUser.isPlatformUser || !isPendingMemberInvite;
    return (
      <Navigate to={isHonoured ? (returnTarget as Routes) : '/'} replace />
    );
  }

  return children;
};

export default AuthenticatedGuard;
